import { STORAGE_KEYS } from '../config';
import { AuthPayload, User } from '../types';

export function persistAuth(payload: AuthPayload): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, payload.accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, payload.refreshToken);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(payload.user));
}

export function clearAuthStorage(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
}

export function loadInitialAuth(): { accessToken?: string; refreshToken?: string; user?: User } {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken) || undefined;
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken) || undefined;
    const user = localStorage.getItem(STORAGE_KEYS.user);
    return {
      accessToken,
      refreshToken,
      user: user ? (JSON.parse(user) as User) : undefined
    };
  } catch (error) {
    clearAuthStorage();
    return {};
  }
}

/**
 * Decode a JWT token and extract its payload without verification.
 * This is safe for client-side expiration checking since we're only
 * reading the expiration time, not trusting the token's claims.
 */
function decodeJWT(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    return null;
  }
}

/**
 * Check if a JWT token is expired.
 * Returns true if token is expired or invalid.
 */
export function isTokenExpired(token: string | undefined): boolean {
  if (!token) return true;

  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  // Add a 60 second buffer to account for clock skew
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return decoded.exp < nowInSeconds + 60;
}
